import { View } from 'react-native';
import {
  cardBackgroundBlack,
  cardBackgroundBlue,
  cardBackgroundGreen,
  cardBackgroundSilver
} from '../../../../data/credentials/background';
import { bcBrandImg, qBrandImg, qBrandBlack } from '../../../../data/credentials/logo';
import { Credential } from '../../../../types/credentials';
import {
  CredentialType,
  CredentialCardBackIssuerDetails,
  CARD_BACK_ISSUER_DETAILS
} from '../index';
import CardHeader from './CardHeader';
import CardInfo from './CardInfo';
import CardBackground from './CardBackground';
import CardBack from './CardBack';

/**
 * @description Card factory
 */
class CardFactory {
  /**
   * Check the type of the credential
   * @param {Credential} credential
   * @return {CredentialType}
   */
  static checkCredentialType: (credential: Credential) => CredentialType = (
    credential: Credential
  ) => {
    if (credential.name.includes(CredentialType.PARKING)) {
      return CredentialType.PARKING;
    } else if (credential.name.includes(CredentialType.BIKE)) {
      return CredentialType.BIKE;
    } else if (credential.name.includes(CredentialType.ACCESS)) {
      return CredentialType.ACCESS;
    } else if (credential.name.includes(CredentialType.QUARTECH)) {
      return CredentialType.QUARTECH;
    } else if (credential.name.includes(CredentialType.BC)) {
      return CredentialType.BC;
    } else {
      return CredentialType.OTHER;
    }
  };

  /**
   * Create the background of the card
   * @param {Credential} credential
   * @return {string}
   */
  static createBackground: (credential: Credential) => string = (credential: Credential) => {
    const backgrounds = {
      [CredentialType.QUARTECH]: cardBackgroundBlack,
      [CredentialType.PARKING]: cardBackgroundBlue,
      [CredentialType.BIKE]: cardBackgroundGreen,
      [CredentialType.ACCESS]: cardBackgroundSilver,
      [CredentialType.BC]: cardBackgroundSilver,
      [CredentialType.OTHER]: cardBackgroundSilver
    };

    const credentialType = this.checkCredentialType(credential);
    return backgrounds[credentialType];
  };

  /**
   * Create the logo of the card
   * @param {Credential} credential
   * @return {React.ReactNode}
   */
  static createLogo: (credential: Credential) => string | null = (credential: Credential) => {
    const credentialType = this.checkCredentialType(credential);

    if (
      credentialType === CredentialType.QUARTECH ||
      credentialType === CredentialType.PARKING ||
      credentialType === CredentialType.BIKE
    ) {
      return qBrandImg;
    } else if (credentialType === CredentialType.ACCESS) {
      return qBrandBlack;
    } else if (credentialType === CredentialType.BC) {
      return bcBrandImg;
    } else {
      return null;
    }
  };

  /**
   * Create the card back details
   * @param {Credential} credential
   * @return {CredentialCardBackIssuerDetails | undefined}
   */
  static createCardBackDetails: (
    credential: Credential
  ) => CredentialCardBackIssuerDetails | undefined = (credential: Credential) => {
    const credentialType = this.checkCredentialType(credential);

    if (credentialType === CredentialType.QUARTECH) {
      return CARD_BACK_ISSUER_DETAILS.find((detail) => detail.type === CredentialType.QUARTECH);
    } else if (credentialType === CredentialType.BC) {
      return CARD_BACK_ISSUER_DETAILS.find((detail) => detail.type === CredentialType.BC);
    } else {
      return CARD_BACK_ISSUER_DETAILS.find((detail) => detail.type === CredentialType.OTHER);
    }
  };

  /**
   * Create the card
   * @param {Credential} credential
   * @param {() => void} [onFlip]
   * @param {boolean} [isFlipped]
   * @param {boolean} [detailsModal]
   * @param {() => void} [detailsModalClose]
   * @return {React.ReactNode}
   */
  static createCard = (
    fullname: string,
    credential: Credential,
    onFlip?: () => void,
    isFlipped?: boolean,
    detailsModal?: boolean,
    detailsModalClose?: () => void
  ): React.ReactNode => {
    if (isFlipped) {
      return (
        <CardBack
          credential={credential}
          onFlip={onFlip}
          detailsModal={detailsModal}
          detailsModalClose={detailsModalClose}
        />
      );
    }

    return (
      <>
        <CardHeader credential={credential} onFlip={onFlip} />
        <CardInfo
          title={fullname}
          subtitle={credential.attributes.find((attr) => attr.name === 'email')?.value || ''}
        />
        <View style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}>
          <CardBackground credential={credential} />
        </View>
      </>
    );
  };
}

export default CardFactory;
